import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import Modal from '@/components/base/Modal';
import { formatMoney } from '@/lib/format';
import type { Offer, Product } from '@/lib/types';

export interface OfferWithProducts extends Offer {
  productIds: string[];
}

interface OfferFormProps {
  open: boolean;
  editing: OfferWithProducts | null;
  allOffers: OfferWithProducts[];
  onClose: () => void;
  onSaved: (offerId: string, offerName: string) => void;
}

interface FormState {
  name: string;
  percentage: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  active: boolean;
}

const emptyForm: FormState = {
  name: '',
  percentage: '',
  startDate: '',
  startTime: '08:00',
  endDate: '',
  endTime: '23:59',
  active: true,
};

const toDateInput = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const toTimeInput = (d: Date) => {
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
};

const buildDateTime = (date: string, time: string) =>
  new Date(`${date || '1970-01-01'}T${time || '00:00'}:00`).toISOString();

export default function OfferForm({ open, editing, allOffers, onClose, onSaved }: OfferFormProps) {
  const [form, setForm] = useState<FormState>(emptyForm);
  const [selected, setSelected] = useState<Product[]>([]);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<Product[]>([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      const start = new Date(editing.starts_at);
      const end = new Date(editing.ends_at);
      setForm({
        name: editing.name,
        percentage: String(editing.percentage),
        startDate: toDateInput(start),
        startTime: toTimeInput(start),
        endDate: toDateInput(end),
        endTime: toTimeInput(end),
        active: editing.active,
      });
    } else {
      setForm(emptyForm);
    }
    setSelected([]);
    setSearch('');
    setResults([]);
    setError(null);
  }, [open, editing]);

  // Carga los productos ya asociados al editar.
  useEffect(() => {
    if (!open || !editing || editing.productIds.length === 0) return;
    let active = true;
    (async () => {
      const { data } = await supabase
        .from('products')
        .select('*')
        .in('id', editing.productIds);
      if (active) setSelected((data as Product[]) ?? []);
    })();
    return () => {
      active = false;
    };
  }, [open, editing]);

  // Búsqueda por nombre, SKU o código de barras.
  useEffect(() => {
    const term = search.trim();
    if (!term) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setSearching(true);
      const { data } = await supabase
        .from('products')
        .select('*')
        .or(`name.ilike.%${term}%,sku.ilike.%${term}%,barcode.ilike.%${term}%`)
        .eq('active', true)
        .limit(12);
      setResults((data as Product[]) ?? []);
      setSearching(false);
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  const addProduct = (p: Product) => {
    setSelected((prev) => (prev.some((x) => x.id === p.id) ? prev : [...prev, p]));
    setSearch('');
    setResults([]);
  };

  const removeProduct = (id: string) => {
    setSelected((prev) => prev.filter((x) => x.id !== id));
  };

  // Detección de conflictos: productos en otra oferta activa con período solapado.
  const conflicts = useMemo(() => {
    const start = buildDateTime(form.startDate, form.startTime);
    const end = buildDateTime(form.endDate, form.endTime);
    const selectedIds = new Set(selected.map((p) => p.id));
    const found: { offer: Offer; products: Product[] }[] = [];
    for (const other of allOffers) {
      if (editing && other.id === editing.id) continue;
      if (!other.active) continue;
      if (end < other.starts_at || start > other.ends_at) continue;
      const overlap = selected.filter((p) => other.productIds.includes(p.id));
      if (overlap.length > 0) found.push({ offer: other, products: overlap });
    }
    return found;
  }, [selected, form.startDate, form.startTime, form.endDate, form.endTime, allOffers, editing]);

  const handleSave = async () => {
    setError(null);
    const percentage = parseFloat(form.percentage);
    if (!form.name.trim()) {
      setError('El nombre de la oferta es obligatorio.');
      return;
    }
    if (Number.isNaN(percentage) || percentage < 0 || percentage > 100) {
      setError('El porcentaje debe estar entre 0 y 100.');
      return;
    }
    if (!form.startDate || !form.endDate) {
      setError('Debes indicar fecha de inicio y de finalización.');
      return;
    }
    const starts_at = buildDateTime(form.startDate, form.startTime);
    const ends_at = buildDateTime(form.endDate, form.endTime);
    if (ends_at <= starts_at) {
      setError('La fecha de finalización debe ser posterior al inicio.');
      return;
    }
    if (selected.length === 0) {
      setError('Selecciona al menos un producto para la oferta.');
      return;
    }
    if (conflicts.length > 0) {
      setError('Hay productos que ya pertenecen a otra oferta activa en el mismo período.');
      return;
    }

    setSaving(true);
    try {
      const offerPayload = {
        name: form.name.trim(),
        percentage,
        starts_at,
        ends_at,
        active: form.active,
      };

      let offerId: string;
      if (editing) {
        offerId = editing.id;
        const { error: e } = await supabase
          .from('offers')
          .update({ ...offerPayload, updated_at: new Date().toISOString() })
          .eq('id', editing.id);
        if (e) throw e;
      } else {
        const { data, error: e } = await supabase
          .from('offers')
          .insert(offerPayload)
          .select('id')
          .single();
        if (e) throw e;
        offerId = (data as Offer).id;
      }

      // Sincroniza productos: borra los que ya no están y agrega los nuevos.
      if (editing) {
        const currentIds = new Set(editing.productIds);
        const newIds = selected.map((p) => p.id);
        const toAdd = newIds.filter((id) => !currentIds.has(id));
        const toRemove = editing.productIds.filter((id) => !newIds.includes(id));
        if (toRemove.length > 0) {
          await supabase.from('offer_products').delete().eq('offer_id', offerId).in('product_id', toRemove);
        }
        if (toAdd.length > 0) {
          await supabase.from('offer_products').insert(
            toAdd.map((pid) => ({ offer_id: offerId, product_id: pid }))
          );
        }
      } else {
        await supabase.from('offer_products').insert(
          selected.map((p) => ({ offer_id: offerId, product_id: p.id }))
        );
      }

      onSaved(offerId, form.name.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar la oferta');
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    'w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400';
  const labelClass = 'block text-sm font-medium text-foreground-800 mb-1.5';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Editar oferta' : 'Nueva oferta'}
      footer={
        <>
          <button
            onClick={onClose}
            disabled={saving}
            className="rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="rounded-md bg-primary-500 hover:bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition disabled:opacity-60 cursor-pointer"
          >
            {saving ? 'Guardando...' : 'Guardar oferta'}
          </button>
        </>
      }
    >
      <div className="space-y-5">
        {/* Información */}
        <div>
          <label className={labelClass}>Nombre de la oferta *</label>
          <input
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            placeholder="Ej. Oferta de fin de semana"
            autoFocus
            className={inputClass}
          />
        </div>

        <div>
          <label className={labelClass}>Porcentaje de descuento (%) *</label>
          <input
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={form.percentage}
            onChange={(e) => setForm((p) => ({ ...p, percentage: e.target.value }))}
            placeholder="Ej. 20"
            className={inputClass}
          />
        </div>

        {/* Fecha y hora */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Fecha de inicio *</label>
            <input
              type="date"
              value={form.startDate}
              onChange={(e) => setForm((p) => ({ ...p, startDate: e.target.value }))}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Hora de inicio</label>
            <input
              type="time"
              value={form.startTime}
              onChange={(e) => setForm((p) => ({ ...p, startTime: e.target.value }))}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Fecha de finalización *</label>
            <input
              type="date"
              value={form.endDate}
              onChange={(e) => setForm((p) => ({ ...p, endDate: e.target.value }))}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Hora de finalización</label>
            <input
              type="time"
              value={form.endTime}
              onChange={(e) => setForm((p) => ({ ...p, endTime: e.target.value }))}
              className={inputClass}
            />
          </div>
        </div>

        {/* Selector de productos */}
        <div>
          <label className={labelClass}>Productos incluidos en la oferta *</label>
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm pointer-events-none" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre, SKU o código de barras..."
              className={`${inputClass} pl-9`}
            />
          </div>

          {searching && <p className="mt-1.5 text-xs text-foreground-500">Buscando...</p>}

          {results.length > 0 && (
            <div className="mt-1.5 rounded-md border border-background-200 divide-y divide-background-200 max-h-52 overflow-y-auto">
              {results.map((p) => {
                const isSelected = selected.some((x) => x.id === p.id);
                return (
                  <button
                    key={p.id}
                    onClick={() => addProduct(p)}
                    className="w-full text-left px-3 py-2 hover:bg-background-100 text-sm cursor-pointer flex items-center justify-between gap-2"
                  >
                    <span className="min-w-0">
                      <span className="font-medium text-foreground-950">{p.name}</span>
                      {p.sku && <span className="ml-2 text-xs text-foreground-500">SKU {p.sku}</span>}
                    </span>
                    <span className="shrink-0 flex items-center gap-2">
                      <span className="text-foreground-500">{formatMoney(p.sale_price, 'RD$')}</span>
                      <i
                        className={`${isSelected ? 'ri-checkbox-circle-fill text-primary-500' : 'ri-add-circle-line text-foreground-400'}`}
                      />
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Productos seleccionados */}
          {selected.length > 0 && (
            <div className="mt-3">
              <p className="text-xs font-medium text-foreground-600 mb-2">
                {selected.length} {selected.length === 1 ? 'producto seleccionado' : 'productos seleccionados'}
              </p>
              <div className="space-y-1.5">
                {selected.map((p) => (
                  <div
                    key={p.id}
                    className="flex items-center justify-between gap-2 rounded-md border border-background-200 bg-background-50 px-3 py-2"
                  >
                    <div className="min-w-0 flex items-center gap-2">
                      <i className="ri-checkbox-circle-fill text-primary-500 shrink-0" />
                      <span className="text-sm font-medium text-foreground-950 truncate">{p.name}</span>
                      <span className="text-xs text-foreground-500 shrink-0">
                        {formatMoney(p.sale_price, 'RD$')}
                      </span>
                    </div>
                    <button
                      onClick={() => removeProduct(p.id)}
                      className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:bg-accent-50 hover:text-accent-700 cursor-pointer shrink-0"
                      title="Quitar"
                    >
                      <i className="ri-close-line" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Conflictos */}
        {conflicts.length > 0 && (
          <div className="rounded-md border border-accent-200 bg-accent-50 p-3 space-y-2">
            <p className="text-sm font-semibold text-foreground-900">
              Productos ya incluidos en otra oferta activa:
            </p>
            {conflicts.map((c) => (
              <div key={c.offer.id} className="text-sm text-foreground-700">
                <span className="font-medium">{c.offer.name}</span> — {Number(c.offer.percentage)}%
                <div className="mt-0.5 text-xs text-foreground-500">
                  {new Date(c.offer.starts_at).toLocaleDateString('es-DO')} →{' '}
                  {new Date(c.offer.ends_at).toLocaleDateString('es-DO')}
                </div>
                <div className="mt-0.5 text-xs">
                  {c.products.map((p) => p.name).join(', ')}
                </div>
              </div>
            ))}
            <p className="text-xs text-foreground-600">
              Quita estos productos de esta oferta para poder guardarla.
            </p>
          </div>
        )}

        <label className="flex items-center justify-between gap-3 rounded-md border border-background-200 p-3 cursor-pointer">
          <span className="text-sm font-medium text-foreground-900">Oferta activa</span>
          <input
            type="checkbox"
            checked={form.active}
            onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))}
            className="w-5 h-5 accent-primary-500 cursor-pointer"
          />
        </label>

        {error && (
          <div className="rounded-md border border-accent-200 bg-accent-50 px-3 py-2.5 text-sm text-foreground-700">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}